import { ActionDataWidgets } from '../types'
import {
  CUSTOM_CRM_TERMS_MAX,
  CUSTOM_CRM_TERMS_MAX_ERROR,
  DESCRIPTION_MAX,
  DESCRIPTION_MAX_ERROR,
  TITLE_MAX,
  TITLE_MAX_ERROR
} from '../validation'
import {
  ContactCollectionFields,
  ContactCollectionForm,
  ContactCollectionFormDisplayTypes,
  FormFields,
  UseContactCollectionFormStateType
} from './types'
import { MaskType } from '@dtx-company/shared-components/src/types'
import { RootPageState } from '@dtx-company/inter-app/src/redux/types'
import { buildActionData } from './utils'
import { defaultWidgetLink } from '../../../constants'
import { getValidatedActionData } from '../typeUtils'
import { hasMailchimp } from '@dtx-company/inter-app/src/redux/slices/pageEditor/mailchimpEditing'
import { setClientHasMailchimpKey } from '@dtx-company/inter-app/src/redux/slices/pageEditor/pageEditorSlice'
import { useClientHasMailchimpKey } from '@dtx-company/app/shared/common/src/hooks/useClientHasMaillchimpKey'
import { useDispatch } from 'react-redux'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLinkThemeState } from '../components/LinkStyle/hooks/useLinkThemeState'
import { usePageSelector } from '@dtx-company/inter-app/src/hooks/usePageSelector'
import { useSetValueWithPreview } from '../utils'
import { useSubmitWidget } from '../submitUtils'
import { v4 as uuid } from 'uuid'
import { MarketingPageRoutes } from '../../../../../../../../packages/true-common/src/constants/routes'

export const useContactCollectionFormState = (
  order: number,
  handleClose: () => void
): UseContactCollectionFormStateType => {
  const { previewLink: c } = usePageSelector((state: RootPageState) => state.linkEditReducer)
  const [mc] = useState(c)
  const curr = mc ?? undefined
  const isNewForm = curr == null

  const id = curr ? curr.id : uuid()
  const validate = ({
    displayName,
    email,
    phone,
    address,
    age,
    gender,
    custom,
    custom2
  }: ContactCollectionFields): boolean | string => {
    if (!(email || phone || address || displayName || custom || custom2 || age || gender)) {
      return 'Please choose at least one field to collect'
    }
    return true
  }

  const { linkTheme, setLinkTheme } = useLinkThemeState({
    defaultLinkTheme: curr?.linkTheme ?? null
  })
  const actionData = getValidatedActionData<'contactCollection'>(curr?.actionData, 'fields')

  const defaultContactCollectionFormValues: ContactCollectionForm = {
    title: curr?.title || '',
    description: curr?.description || '',
    thumbNailImgUrl: curr?.thumbNailImgUrl || '',
    fields: {
      displayName: isNewForm ? true : actionData?.fields.includes('dname') || false,
      email: isNewForm ? true : actionData?.fields.includes('email') || false,
      phone: actionData?.fields.includes('phone') || false,
      address: actionData?.fields.includes('address') || false,
      age: actionData?.fields.includes('age') || false,
      gender: actionData?.fields.includes('gender') || false,
      custom: actionData?.fields.includes('custom') || false,
      custom2: actionData?.fields.includes('custom2') || false
    },
    required: {
      displayName: isNewForm ? true : (actionData?.required ?? []).includes('dname') || false,
      email: isNewForm ? true : (actionData?.required ?? []).includes('email') || false,
      phone: (actionData?.required ?? []).includes('phone') || false,
      address: (actionData?.required ?? []).includes('address') || false,
      age: (actionData?.required ?? []).includes('age') || false,
      gender: (actionData?.required ?? []).includes('gender') || false,
      custom: (actionData?.required ?? []).includes('custom') || false,
      custom2: (actionData?.required ?? []).includes('custom2') || false
    },
    customFieldLabel: actionData?.customFieldLabel || '',
    customFieldLabel2: actionData?.customFieldLabel2 || '',
    customTerms: actionData?.customTerms
      ? {
          ...actionData.customTerms,
          pdf: actionData?.link ? actionData?.link : MarketingPageRoutes.TERMS_OF_USE
        }
      : { enabled: false },
    zapier: actionData?.zapier || { enabled: false },
    klaviyo: actionData?.klaviyo || { enabled: false },
    hubspot: actionData?.hubspot || { enabled: false, apiKey: '' },
    mailChimp: actionData?.mailChimp || { enabled: false },
    googleSheets: actionData?.googleSheets || { enabled: false },
    widgetDisplayType: actionData?.widgetDisplayType ?? MaskType.NO_IMAGE,
    displayType: curr?.displayType || ContactCollectionFormDisplayTypes.EMBED,
    successMessage: curr?.actionData.successMessage || { enabled: false },
    filledOutFormRequirement: curr?.actionData.filledOutFormRequirement || false,
    disableConfetti: curr?.actionData.disableConfetti || false
  }

  const {
    register,
    setValue: setFormValue,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setError,
    watch
  } = useForm({
    defaultValues: defaultContactCollectionFormValues
  })

  const dispatch = useDispatch()
  const mailchimpApiKey = useClientHasMailchimpKey()
  const submitWidget = useSubmitWidget()
  const pageId = usePageSelector((state: RootPageState) => state.pageEditorReducer.pageId) ?? ''

  useEffect(() => {
    if (mailchimpApiKey) return

    const getHasMailchimp = async (): Promise<void> => {
      const resp = await hasMailchimp(pageId)
      const mcKey = typeof resp === 'string' ? resp : ''
      dispatch(setClientHasMailchimpKey({ apiKey: mcKey }))
    }
    getHasMailchimp()
  }, [pageId, dispatch, mailchimpApiKey])

  const onSubmit = async (props: ContactCollectionForm): Promise<void> => {
    try {
      const actionData = buildActionData(props)
      await submitWidget({
        curr,
        actionData,
        handleClose,
        widgetType: (curr ? curr.type : 'contactCollection') as ActionDataWidgets,
        image: props.thumbNailImgUrl,
        linkTheme,
        fields: {
          title: props.title,
          description: props.description,
          id,
          order,
          displayType: props.displayType
        }
      })
    } catch (e) {
      console.error(e)
    }
  }
  const watchAll = watch()

  useEffect(() => {
    register(FormFields.TITLE, {
      required: true,
      maxLength: {
        value: TITLE_MAX,
        message: TITLE_MAX_ERROR
      }
    })
    register(FormFields.IMAGE)
    register(FormFields.DISPLAY_TYPE, {
      required: true
    })
    register(FormFields.WIDGET_DISPLAY_TYPE)
    register(FormFields.DESCRIPTION, {
      maxLength: {
        value: DESCRIPTION_MAX,
        message: DESCRIPTION_MAX_ERROR
      }
    })
    register(FormFields.FIELDS, {
      validate: (value: ContactCollectionFields): string | boolean => validate(value)
    })
    register(FormFields.REQUIRED)
    register(FormFields.CUSTOM_FIELD_LABEL, {
      maxLength: {
        value: TITLE_MAX,
        message: TITLE_MAX_ERROR
      }
    })
    register(FormFields.CUSTOM_FIELD_LABEL_2, {
      maxLength: {
        value: TITLE_MAX,
        message: TITLE_MAX_ERROR
      }
    })
    register(FormFields.ZAPIER)
    register(FormFields.GOOGLESHEETS)
    register(FormFields.KLAVIYO)
    register(FormFields.HUBSPOT, {
      validate: ({ apiKey, enabled }) => {
        if (enabled) {
          if (!apiKey) {
            return 'A HubSpot API key is required'
          }
        }
        return true
      }
    })
    register(FormFields.CUSTOM_TERMS, {
      validate: ({ enabled, disclaimer, pdf }) => {
        if (enabled) {
          const { length } = disclaimer ?? { length: 0 }

          if (length > CUSTOM_CRM_TERMS_MAX) {
            return CUSTOM_CRM_TERMS_MAX_ERROR
          }

          if (!pdf) {
            return 'Please provide a URL to, upload a PDF or write a disclaimer with your T&Cs'
          }
        }

        return true
      }
    })
    register(FormFields.IMAGE)
    register(`${FormFields.SUCCESS_MESSAGE}.enabled`)
    register(`${FormFields.SUCCESS_MESSAGE}.header`, {
      maxLength: {
        value: TITLE_MAX,
        message: TITLE_MAX_ERROR
      }
    })
    register(`${FormFields.SUCCESS_MESSAGE}.body`, {
      maxLength: {
        value: DESCRIPTION_MAX,
        message: DESCRIPTION_MAX_ERROR
      }
    })
    register(FormFields.FILLED_OUT_FORM_REQ)
    register(FormFields.DISABLE_CONFETTI)
  }, [register])

  useEffect(() => {
    register(FormFields.MAILCHIMP, {
      validate: ({ enabled }) => {
        if (enabled && !mailchimpApiKey) {
          return 'A Mailchimp API key is required'
        }
        return true
      }
    })
  }, [mailchimpApiKey, register])

  const disabled =
    watchAll.title.length === 0 ||
    !validate(watchAll.fields) ||
    (watchAll.fields.custom && watchAll.customFieldLabel === '') ||
    (watchAll.fields.custom2 && watchAll.customFieldLabel2 === '') ||
    Boolean(errors[FormFields.DISPLAY_TYPE]) ||
    !!errors.fields

  const edit = Boolean(curr)

  const previewLink = useMemo(
    () => ({
      ...defaultWidgetLink,
      type: 'contactCollection',
      title: watchAll.title,
      id,
      linkTheme,
      thumbNailImgUrl: watchAll.thumbNailImgUrl,
      description: watchAll.description,
      displayType: watchAll.displayType,
      actionData: buildActionData(watchAll)
    }),
    [watchAll, id, linkTheme]
  )

  const { setValue, setLinkThemeValue } = useSetValueWithPreview(
    previewLink,
    setFormValue,
    setLinkTheme
  )

  return {
    id,
    handleSubmit,
    onSubmit,
    setValue,
    watchAll,
    disabled,
    setLinkThemeValue,
    linkTheme,
    edit,
    errors,
    setError,
    clearErrors
  }
}

export const shouldProGateThisCRMFeature = (hasFeatureAccess: boolean): boolean => {
  return !hasFeatureAccess
}

export const useCheckApiKey = ({
  checkAccountCallback,
  initApiKey
}: {
  checkAccountCallback: () => Promise<string | null>
  initApiKey: string | null
}): {
  existingApiKey: string | null
  loading: boolean
} => {
  const [existingApiKey, setExistingApiKey] = useState<string | null>(initApiKey ?? '')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAccountHasApiKey = async (): Promise<void> => {
      try {
        const apiKey = await checkAccountCallback()
        setExistingApiKey(apiKey ?? null)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    fetchAccountHasApiKey()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { existingApiKey, loading }
}
